import React from 'react';
import { usePayDisplay, isLockedRate } from '../shared/PayLabel';
import { AlertCircle, Award, Bookmark, Briefcase, Calendar, Clock, DollarSign, Filter, MapPin, RefreshCw, Search, Star, Zap } from 'lucide-react';

// Extracted from WorkerDashboard.js — receives everything it needs via `ctx`.
export default function WorkerJobsTab({ ctx }) {
  const { AdvancedJobFilters, DistanceUnitToggle, activeTab, distanceUnit, earnings, filters, gradients, handleDisableLocation, handlePickJob, handleSaveJob, hideBelowMin, isBelowWorkerMin, isLowPayJob, jobs, jobsMeta, loadJobs, loadLocation, loading, location, platformMinPay, setDistanceUnit, setFilters, setHideBelowMin, setLowPayConfirmJob, setShowFilters, showFilters, visibleJobs, workerMinRate } = ctx;
  const { rateText, totalText } = usePayDisplay();
  const radiusKm = Math.min(Number(filters?.radius_km) || 50, jobsMeta?.effectiveRadiusKm || Infinity);
  const radiusText = distanceUnit === 'mi' ? `${Math.round(radiusKm / 1.609)} miles` : `${Math.round(radiusKm)} km`;
  const locationRequired = !!jobsMeta?.locationRequired && !location;
  return (
    <>
    {activeTab === 'jobs' && (
      <div className="space-y-6">
        {/* Header - matches legacy worker portal */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-slate-100 rounded-xl">
                <Search className="w-6 h-6 text-slate-700" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-slate-800">
                  {location ? 'Jobs Near You' : 'Find Your Next Opportunity'}
                </h2>
                <p className="text-slate-500 text-sm">
                  {jobs.length} available opportunities{location ? ` within ${radiusText}` : ''}. Apply with one tap and start earning today.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`px-5 py-2.5 rounded-xl font-medium text-sm transition inline-flex items-center gap-2 ${
                  showFilters 
                    ? 'bg-slate-800 text-white' 
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                data-testid="toggle-filters-btn"
              >
                <Filter className="w-4 h-4" />
                {showFilters ? 'Hide Filters' : 'Filter Jobs'}
              </button>
              <button
                onClick={() => setHideBelowMin(!hideBelowMin)}
                className={`px-5 py-2.5 rounded-xl font-medium text-sm transition inline-flex items-center gap-2 ${
                  hideBelowMin
                    ? 'bg-emerald-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                title="Hide jobs paying below your minimum rate preference or the platform minimum"
                data-testid="hide-low-pay-toggle"
              >
                <DollarSign className="w-4 h-4" />
                {hideBelowMin ? 'Fair Pay Only' : 'Hide Low Pay'}
              </button>
              <button
                onClick={loadJobs}
                className="px-5 py-2.5 bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 font-medium text-sm transition inline-flex items-center gap-2"
                data-testid="refresh-jobs-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              {!location ? (
                <button
                  onClick={() => loadLocation({ forceFresh: true })}
                  className="px-5 py-2.5 bg-white text-blue-600 border border-blue-200 rounded-xl font-medium text-sm hover:bg-blue-50 transition inline-flex items-center gap-2"
                  data-testid="enable-location-btn"
                >
                  <MapPin className="w-4 h-4" />
                  Enable Location
                </button>
              ) : (
                <button
                  onClick={handleDisableLocation}
                  className="px-5 py-2.5 bg-white text-emerald-700 border border-emerald-200 rounded-xl font-medium text-sm hover:bg-emerald-50 transition inline-flex items-center gap-2"
                  data-testid="disable-location-btn"
                  title="Location is on — click to turn it off for this session"
                >
                  <MapPin className="w-4 h-4" />
                  Disable Location
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Stats Bar - matches legacy */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-100 rounded-lg">
                <Briefcase className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{jobs.length}</p>
                <p className="text-sm text-gray-500">Available Jobs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  ${jobs.length > 0 ? Math.round(jobs.reduce((sum, j) => sum + (j.pay_rate || 0), 0) / jobs.length) : 0}
                </p>
                <p className="text-sm text-gray-500">Avg Hourly Rate</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-100 rounded-lg">
                <Star className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">4.8</p>
                <p className="text-sm text-gray-500">Your Rating</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Award className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{earnings?.shifts_completed || 0}</p>
                <p className="text-sm text-gray-500">Completed Shifts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Location Warning */}
        {!location && (
          <div className={`flex items-center gap-3 p-4 border rounded-xl ${locationRequired ? 'bg-rose-50 border-rose-200' : 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200'}`} data-testid={locationRequired ? 'location-required-notice' : 'location-disabled-notice'}>
            <div className={`p-2 rounded-lg ${locationRequired ? 'bg-rose-100' : 'bg-amber-100'}`}>
              <AlertCircle className={`w-5 h-5 ${locationRequired ? 'text-rose-600' : 'text-amber-600'}`} />
            </div>
            <p className={`text-sm flex-1 ${locationRequired ? 'text-rose-800' : 'text-amber-800'}`}>
              {locationRequired
                ? 'Your organisation requires location to browse shifts. Enable location to see shifts near you.'
                : 'Location access not enabled. Showing all jobs. Enable location to see distance and nearby opportunities.'}
            </p>
            {locationRequired && (
              <button onClick={() => loadLocation({ forceFresh: true })} className="px-3 py-1.5 bg-rose-600 text-white rounded-lg text-xs font-semibold hover:bg-rose-700" data-testid="location-required-enable-btn">
                Enable Location
              </button>
            )}
          </div>
        )}
        
        {/* Filters Panel */}
        {/* Iter187: DistanceUnitToggle drives BOTH the "km/mi away"
            labels on job cards AND the Radius input inside
            AdvancedJobFilters (they were previously desynced). */}
        <div className="flex justify-end">
          <DistanceUnitToggle unit={distanceUnit} onChange={setDistanceUnit} />
        </div>
        {showFilters && (
          <AdvancedJobFilters
            filters={filters}
            onFiltersChange={setFilters}
            onSearch={loadJobs}
            unit={distanceUnit}
            maxRadiusKm={jobsMeta?.effectiveRadiusKm || null}
          />
        )}
        {hideBelowMin && jobs.length !== visibleJobs.length && (
          <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl" data-testid="low-pay-hidden-banner">
            <DollarSign className="w-5 h-5 text-emerald-600 flex-shrink-0" />
            <p className="text-sm text-emerald-800">
              {jobs.length - visibleJobs.length} low-pay job{jobs.length - visibleJobs.length === 1 ? '' : 's'} hidden (below {workerMinRate ? `your $${workerMinRate}/hr minimum` : `the platform minimum${platformMinPay ? ` of $${platformMinPay}/hr` : ''}`}).
            </p>
          </div>
        )}
        {loading ? (
          <div className="text-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading opportunities...</p>
          </div>
        ) : visibleJobs.length === 0 ? (
          <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-2xl p-12 text-center border-2 border-dashed border-blue-200">
            <div className="inline-flex p-5 bg-blue-100 rounded-2xl mb-4">
              <Briefcase className="w-12 h-12 text-blue-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Jobs Found</h3>
            <p className="text-gray-600 mb-6">Try adjusting your filters or expanding your search area</p>
            <button
              onClick={loadJobs}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-500 text-white rounded-xl hover:shadow-lg font-medium transition-all duration-200 inline-flex items-center gap-2"
            >
              <RefreshCw className="w-5 h-5" />
              Refresh Jobs
            </button>
          </div>
        ) : (
          <div className="grid gap-6">
            {visibleJobs.map((job, index) => {
              const gradient = gradients[index % gradients.length];
              const jobStatusBadge = (() => {
                if (job.already_applied) return { label: `Applied · ${(job.application_status || 'pending').replace('_', ' ')}`, cls: 'bg-blue-50 text-blue-700 border-blue-200' };
                if (job.previously_rejected && !job.worker_can_reapply) return { label: 'Previously declined — cannot re-apply', cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                if (job.previously_rejected && job.worker_can_reapply) return { label: 'Previously declined — reconsider', cls: 'bg-amber-50 text-amber-700 border-amber-200' };
                if (job.acceptance_window_open === false) {
                  // iter174: distinguish "not yet open" (future shift) from
                  // "already closed" (missed / past) so future jobs don't
                  // read as rejections.
                  if (job.acceptance_window_reason === 'ACCEPTANCE_WINDOW_NOT_OPEN') {
                    return { label: 'Opens closer to shift', cls: 'bg-blue-50 text-blue-700 border-blue-200' };
                  }
                  if (job.acceptance_window_reason === 'SHIFT_ENDED') {
                    return { label: 'Shift already ended', cls: 'bg-slate-100 text-slate-500 border-slate-200' };
                  }
                  return { label: 'Acceptance window closed', cls: 'bg-red-50 text-red-700 border-red-200' };
                }
                return { label: 'Available', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' };
              })();
              const canApply =
                !job.already_applied &&
                (job.worker_can_reapply !== false) &&
                (job.acceptance_window_open !== false);

              return (
                <div 
                  key={job._id} 
                  className="group relative bg-white rounded-2xl border border-gray-200 hover:border-transparent hover:shadow-2xl transition-all duration-300 overflow-hidden"
                  data-testid="job-card"
                >
                  {/* Gradient accent bar */}
                  <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient} opacity-0 group-hover:opacity-100 transition-opacity`}></div>
                  
                  <div className="p-6">
                    <div className="flex flex-col md:flex-row md:justify-between gap-4">
                      {/* Job Info */}
                      <div className="flex-1 min-w-0">
                        {/* Status badge */}
                        <div className="flex items-center gap-2 mb-3">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${jobStatusBadge.cls}`}
                            data-testid={`job-status-badge-${job._id}`}
                          >
                            <span className="w-1.5 h-1.5 rounded-full bg-current"></span>
                            {jobStatusBadge.label}
                          </span>
                        </div>
                        {/* Job Title & Employer */}
                        <div className="flex items-start gap-4 mb-4">
                          <div className={`p-3 bg-gradient-to-br ${gradient} rounded-xl flex-shrink-0 group-hover:scale-110 transition-transform`}>
                            <Briefcase className="w-6 h-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-1 group-hover:text-blue-600 transition-colors break-words line-clamp-2" title={job.title}>
                              {job.title}
                            </h3>
                            <p className="text-sm font-medium text-gray-600 flex items-center gap-2 min-w-0">
                              <span className="inline-block w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
                              <span className="truncate" title={job.employer_name}>{job.employer_name}</span>
                            </p>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-gray-700 mb-4 leading-relaxed line-clamp-2">
                          {job.description}
                        </p>

                        {/* Job Details */}
                        <div className="flex flex-wrap gap-3 mb-4">
                          {job.distance_km && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                              <MapPin className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-700" data-testid="job-distance">
                                {distanceUnit === 'mi' ? `${job.distance_mi} mi away` : `${job.distance_km} km away`}
                              </span>
                            </div>
                          )}
                          {rateText(job) !== null && (
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-lg border border-emerald-100">
                            <DollarSign className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm font-semibold text-emerald-700" data-testid="job-card-rate" data-locked-rate={isLockedRate(job) ? 'true' : undefined}>
                              {rateText(job)}
                            </span>
                          </div>
                          )}
                          {job.below_platform_min && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-lg border border-red-200" data-testid="below-platform-min-badge">
                              <AlertCircle className="w-4 h-4 text-red-600" />
                              <span className="text-sm font-semibold text-red-700">
                                Below Minimum Pay{platformMinPay ? ` ($${platformMinPay}/hr min)` : ''}
                              </span>
                            </div>
                          )}
                          {!job.below_platform_min && isBelowWorkerMin(job) && (
                            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-lg border border-amber-200" data-testid="below-worker-min-badge">
                              <AlertCircle className="w-4 h-4 text-amber-600" />
                              <span className="text-sm font-semibold text-amber-700">
                                Below Your Minimum Rate (${workerMinRate}/hr)
                              </span>
                            </div>
                          )}
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                            <Clock className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-medium text-purple-700">
                              {Math.round((job.duration_hours || 0) * 10) / 10}h shift
                            </span>
                          </div>
                          <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border border-orange-100">
                            <Calendar className="w-4 h-4 text-orange-600" />
                            <span className="text-sm font-medium text-orange-700">
                              {new Date(job.start_time).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {/* Skills */}
                        {job.required_skills && job.required_skills.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {job.required_skills.map((skill, idx) => (
                              <span
                                key={`${skill}-${idx}`}
                                className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Pay & Action */}
                      <div className="flex md:flex-col items-center md:items-end gap-4 flex-shrink-0">
                        {totalText(job) !== null && (
                        <div className={`px-6 py-4 bg-gradient-to-br ${gradient} rounded-xl text-white text-center shadow-lg group-hover:scale-105 transition-transform`}>
                          <p className="text-xs font-medium opacity-90 mb-1">{isLockedRate(job) ? 'Pay' : 'Total Pay'}</p>
                          <p className={`${isLockedRate(job) ? 'text-sm' : 'text-2xl'} font-bold tabular-nums`} data-testid="job-card-total">
                            {totalText(job)}
                          </p>
                        </div>
                        )}
                        
                        <div className="flex gap-2 flex-wrap justify-end">
                          <button
                            onClick={() => handleSaveJob(job._id)}
                            className="p-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-blue-50 hover:text-blue-600 transition-colors"
                            title="Save for later"
                            data-testid="job-save-button"
                          >
                            <Bookmark className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => canApply && (isLowPayJob(job) ? setLowPayConfirmJob(job) : handlePickJob(job._id))}
                            disabled={!canApply}
                            className={`px-4 sm:px-6 py-3 rounded-xl font-semibold flex items-center gap-2 whitespace-nowrap transition-all duration-200 ${
                              canApply
                                ? `bg-gradient-to-r ${gradient} text-white hover:shadow-lg transform hover:-translate-y-0.5`
                                : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                            }`}
                            data-testid="job-accept-button"
                            title={canApply
                              ? 'Accept this shift'
                              : (job.acceptance_window_message || jobStatusBadge.label)}
                          >
                            <Zap className="w-5 h-5 flex-shrink-0" />
                            {job.already_applied
                              ? 'Applied'
                              : job.acceptance_window_open === false
                              ? (job.acceptance_window_reason === 'ACCEPTANCE_WINDOW_NOT_OPEN'
                                  ? 'Opens later'
                                  : job.acceptance_window_reason === 'SHIFT_ENDED'
                                  ? 'Ended'
                                  : 'Closed')
                              : job.previously_rejected && job.worker_can_reapply
                              ? 'Re-apply'
                              : job.previously_rejected
                              ? 'Declined'
                              : 'Accept Shift'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}
    </>
  );
}
